import { MAX_CONCURRENT_STREAMS } from "@/composables/useSharedCameraStream";
import { useToastService } from "@/composables/useToastService.js";

// On <body> so it also reaches viewer windows, which are teleported out of the grid.
const HIGHLIGHT_CLASS = "stream-limit-warning";
const TOAST_LIFE_MS = 4000;

// Id of the stream-limit toast on screen, if any.
let visibleToastId: string | null = null;
let clearTimer: ReturnType<typeof setTimeout> | undefined;
let nextToastId = 0;

function clearHighlight(): void {
    clearTimeout(clearTimer);
    visibleToastId = null;
    document.body.classList.remove(HIGHLIGHT_CLASS);
}

/**
 * Warn that no stream slot is free, and outline the controls that free one
 * (.stream-limit-target) for as long as the toast stays up.
 */
export function showStreamLimitWarning(): void {
    // One at a time: a burst of blocked presses would stack identical toasts.
    if (visibleToastId) return;

    const id = `stream-limit-${nextToastId++}`;
    visibleToastId = id;
    document.body.classList.add(HIGHLIGHT_CLASS);
    // Toasts don't pause on hover, so this tracks the toast's life and still
    // clears if it is removed without a close event.
    clearTimer = setTimeout(clearHighlight, TOAST_LIFE_MS);

    useToastService().showToast({
        id,
        severity: "warn",
        summary: "Stream limit reached",
        detail: `Only ${MAX_CONCURRENT_STREAMS} cameras can stream at once. Stop one stream or close one camera to open another.`,
        life: TOAST_LIFE_MS,
    });
}

/** Wired to the app's <Toast> close event, for a toast dismissed early. */
export function onToastRemoved({ message }: { message: { id?: unknown } }) {
    if (message.id === visibleToastId) clearHighlight();
}
